import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AIRTABLE_URL, AIRTABLE_HEADERS } from "./constants";

export default function History() {
  const [history, setHistory] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const navigate = useNavigate();

  const technicianPhone = localStorage.getItem("technicianPhone")?.trim();

 useEffect(() => {
  const fetchData = async () => {
    try {
      const res = await fetch(`${AIRTABLE_URL}`, {
        headers: AIRTABLE_HEADERS,
      });
      const data = await res.json();

      const records = data.records
        .map((rec) => ({
          id: rec.id,
          ...rec.fields,
        }))
        .filter(
          (rec) =>
            rec.assignedTechnicianPhone?.trim() === technicianPhone &&
            ["true", "TRUE", true].includes(rec.paid)
        );

      setHistory(records);
      localStorage.setItem("technicianHistory", JSON.stringify(records));

      const ratingRes = await fetch(`${AIRTABLE_URL}`, {
        headers: AIRTABLE_HEADERS,
      });
      const ratingData = await ratingRes.json();
      const ratingRecords = ratingData.records.map((rec) => rec.fields);

      setRatings(ratingRecords);
      localStorage.setItem("ratingsData", JSON.stringify(ratingRecords));
    } catch (err) {
      console.error("Error fetching Airtable data:", err);
    }
  };

  // ✅ Always fetch latest data
  if (technicianPhone) fetchData();
}, [technicianPhone]);

const parseServiceTotal = (servicesStr) => {
  if (!servicesStr) return 0;

  // Match ₹500, ₹500x2, ₹500 x 2
  const matches = [...servicesStr.matchAll(/₹\s?(\d+)(?:\s?[xX]\s?(\d+))?/g)];
  if (!matches) return 0;

  return matches.reduce((sum, match) => {
    const price = parseInt(match[1], 10);
    const qty = match[2] ? parseInt(match[2], 10) : 1; // default quantity = 1
    return sum + price * qty;
  }, 0);
};



  const parseExtraPartsTotal = (extraPartsStr) => {
    if (!extraPartsStr || extraPartsStr === "None") return 0;
    return extraPartsStr.split(",").reduce((sum, part) => {
      const match = part.match(/₹\s?(\d+)/);
      return sum + (match ? parseInt(match[1], 10) : 0);
    }, 0);
  };

  const formatExtraParts = (extraPartsStr) =>
    extraPartsStr && extraPartsStr !== "None" ? extraPartsStr : "None";

  const generatePDF = (item, serviceTotal, extraPartsTotal, grandTotal) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Technician Service Report", 14, 20);

    const tableData = [
      ["Order ID", item.orderId],
      ["Customer", item.name],
      ["Phone", item.phone],
      ["Address", item.address],
      ["Services", item.services],
      ["Total Service Cost", `₹${serviceTotal}`],
      ["Extra Parts", formatExtraParts(item.extraParts)],
      ["Extra Parts Cost", `₹${extraPartsTotal}`],
      ["Grand Total", `₹${grandTotal}`],
      ["Approved", item.approved === "TRUE" ? "Yes ✅" : "No ❌"],
    ];

    autoTable(doc, {
      startY: 30,
      head: [["Field", "Details"]],
      body: tableData,
    });

    doc.save(`Order_${item.orderId}.pdf`);
  };

  const getRatingForOrder = (orderId) => {
    return ratings.find((r) => r.orderId === orderId);
  };

  return (
    <div style={{ padding: 20, position: "relative" }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "none",
          border: "none",
          fontSize: 20,
          cursor: "pointer",
          color: "Black",
        }}
      >
        ✖
      </button>

      <h2 style={{ marginTop: 40 }}>🧾 Technician History</h2>

      {history.length === 0 ? (
        <p>No paid history found for this technician.</p>
      ) : (
        history.map((item, index) => {
          const serviceTotal = parseServiceTotal(item.services);
          const extraPartsTotal = parseExtraPartsTotal(item.extraParts);
          const grandTotal = serviceTotal + extraPartsTotal;

          const ratingEntry = getRatingForOrder(item.orderId);

          return (
            <div
              key={`${item.orderId}-${index}`}
              style={{
                border: "1px solid #ccc",
                padding: 10,
                marginBottom: 10,
                background: "#f9f9f9",
                borderRadius: 6,
              }}
            >
              <div
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <span>
                  <b>Order ID:</b> {item.orderId}
                </span>
                <div>
                  <button
                    onClick={() =>
                      setOpenIndex(openIndex === index ? null : index)
                    }
                    style={{
                      backgroundColor: "#007bff",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      padding: "3px 8px",
                      cursor: "pointer",
                      marginRight: 6,
                    }}
                  >
                    {openIndex === index ? "Hide" : "View"}
                  </button>

                  <button
                    onClick={() =>
                      generatePDF(item, serviceTotal, extraPartsTotal, grandTotal)
                    }
                    style={{
                      backgroundColor: "#28a745",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      padding: "3px 8px",
                      cursor: "pointer",
                      marginRight: 6,
                    }}
                  >
                    📄 PDF
                  </button>
                </div>
              </div>

              {openIndex === index && (
                <div style={{ marginTop: 10 }}>
                  <p>
                    <b>Customer:</b> {item.name}
                  </p>
                  <p>
                    <b>Phone:</b> {item.phone}
                  </p>
                  <p>
                    <b>Address:</b> {item.address}
                  </p>
                  <p>
                    <b>Services:</b> {item.services}
                  </p>
                  <p>
                    <b>Total Service Cost:</b> ₹{serviceTotal}
                  </p>
                  <p>
                    <b>Extra Parts:</b> {formatExtraParts(item.extraParts)}
                  </p>
                  <p>
                    <b>Extra Parts Cost:</b> ₹{extraPartsTotal}
                  </p>
                  <p>
                    <b>Grand Total:</b> ₹{grandTotal}
                  </p>
                  <p>
                    <b>Status:</b> {item.status}
                  </p>

                  {/* ✅ Rating */}
                  {ratingEntry ? (
                    <div style={{ marginTop: 10 }}>
                      <p>
                        <b>⭐ Rating:</b> {ratingEntry.rating} / 5
                      </p>
                      <p>
                        <b>📝 Feedback:</b> {ratingEntry.feedback}
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() =>
                        navigate(`/rate-technician/${item.orderId}`)
                      }
                      style={{
                        marginTop: 10,
                        backgroundColor: "#ffc107",
                        color: "black",
                        padding: "6px 10px",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      ✍️ Give Rating
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
